import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import useIsMobile from '../../../../../../hooks/useIsMobile';
import { MeasurementCode } from '../../types';
import MedicalResultsService, {
  Ranges,
  ResultHistory,
  MedicalResultSource,
} from '../../../../../../services/MedicalResultsService';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useFeatures,
  useService,
} from '../../../../../../hooks';
import { useAsync } from 'react-use';
import { TimeScale, TimeScalePicker } from './TimeScalePicker';
import { Datum } from 'victory';
import { ResultChart } from './MedicalResultsChart';
import { MultiReferenceRange } from './MutliReferenceRange';
import moment from 'moment';
import MarkdownViewer from '../../../../../MarkdownViewer';
import {
  getObservationComment,
  getObservationHHmmDate,
  getObservationMMMYYYYDate,
  getObservationNumericValue,
  getObservationValueDisplay,
  groupMedicalResultsByUnit,
  observationPrimaryValueIsValueString,
} from '../../util/fhir';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { getColorByStatus } from '../../util/graphUtils';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { MessageDescriptor, useIntl } from 'react-intl';
import ColorContext from '../../ColorContext';
import { ValueBadge } from './ValueBadge';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { ArrowLeft, ArrowRight } from '@lifeomic/chromicons';
import { messages } from './constants';
import clsx from 'clsx';
import { Skeleton } from '@lifeomic/chroma-react/components/Skeleton';
import { UnitPicker } from './UnitPicker';
import { formatResultHistoryCodingForDisplay } from '../../../LabResults/formatters';
import { useReportInfoToBugsnag } from '../../../../../../util/bugsnagReporting';
import {
  RadioGroupMinimal,
  Radio,
} from '@lifeomic/chroma-react/components/Radio';
import {
  ResultViewMode,
  hasMeaningfulPersonalizedDetail,
  getAnnotatedHistoryDisplayModel,
  getEffectiveStatusForLens,
} from '../../util/annotated-results';

export const SYSTEM_LIFEOMIC_DOCUMENT_TYPE =
  'http://lifeomic.com/fhir/document-type';
export const getLabFromObservation = (observation: fhir.Observation) =>
  observation?.meta?.tag?.find(
    tag => tag.system === SYSTEM_LIFEOMIC_DOCUMENT_TYPE,
  )?.code;

/**
 * Converts the lab name from the observation to a formatted name
 * if the lab is not specified, it will return 'Unspecified'
 * if the lab is not 'quest' or 'boston-heart' will return the raw lab name
 * @param observation
 * @returns
 */
const getFormattedLabName = (
  observation: fhir.Observation,
  formatMessage: (message: MessageDescriptor) => string,
) => {
  const lab = getLabFromObservation(observation) || ''; // 'quest' | 'boston-heart'
  switch (lab) {
    case 'Unspecified':
      return formatMessage(messages.unspecified);
    case 'quest':
      return formatMessage(messages.quest);
    case 'boston-heart':
      return formatMessage(messages.bostonHeart);
    default:
      return lab;
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  modalContent: {
    maxHeight: theme.pxToRem(800),
    backgroundColor: theme.palette.common.white,
  },
  root: {
    minHeight: '100%',
    position: 'relative',
  },
  topSection: {
    '& fieldset': {
      width: '100%',
      margin: `0 ${theme.pxToRem(12)}`,
    },
    // This is needed to allow the top section to shrink when the content is too small
    minHeight: 0,
  },
  multiRangeContainer: {
    minHeight: 0,
  },
  noResultsText: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  valueRoot: {
    marginBottom: theme.spacing(2),
  },
  resultPicker: {
    '& button': {
      width: theme.pxToRem(200),
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey[500]}`,
    },
  },
  resultPickerLab: {
    color: theme.palette.black[300],
  },
  selectionOptionBox: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr',
    '& h6': {
      fontSize: 20,
    },
  },
  selectedResultDropdownSelector: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  selectedResultBox: {
    position: 'relative',
    '&:first-of-type::after': {
      background: theme.palette.primary.main,
      content: `''`,
      left: theme.spacing(-2),
      position: 'absolute',
      height: `calc(100% + ${theme.spacing(1)})`,
      top: theme.spacing(-0.5),
      width: theme.pxToRem(4),
    },
  },
  selectedResultText: {
    color: theme.palette.primary.main,
  },
  inlineTitle: {
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  skeleton: {}, // Empty class to allow for (&:has($skeleton)) conditional styling below
  inlineRangeView: {
    minHeight: theme.pxToRem(100),
    '@media print': {
      '&': {
        minHeight: 'unset',
        breakInside: 'avoid',
      },
    },
    flex: 1,
    '&:last-of-type': {
      marginBottom: theme.spacing(2),
    },
    '& + $inlineRangeView': {
      marginBottom: 0,
    },
    '& + span': {
      '&, & + span': {
        display: 'none',
      },
    },
    '&:has($skeleton)': {
      marginTop: theme.spacing(2),
    },
  },
  inlineValueAndCommentRow: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    width: '100%',
  },
  lensToggle: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  providerQuote: {
    backgroundColor: theme.palette.grey[50],
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  providerQuoteText: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
}));

/** Observation comment body: optional line clamp for compact layouts (prop-driven, no inline style object). */
const useObservationCommentTextStyles = makeStyles<{ lineClamp?: number }>(
  (_theme: Theme) => ({
    observationCommentText: ({ lineClamp }) => ({
      whiteSpace: 'pre-wrap',
      ...(lineClamp != null
        ? {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: lineClamp,
            overflow: 'hidden',
            wordBreak: 'break-word',
          }
        : {}),
    }),
  }),
);

/** When inline clinical-impression charts show “No results”, report to Bugsnag with this context. */
export type ClinicalImpressionAttachmentNoResultsBugsnagContext = {
  code: string;
  system: string;
  patientId?: string;
  source?: MedicalResultSource;
  context: 'clinical-impression-attachment';
};

interface RangeViewContentProps {
  initialSelectedResourceId?: string;
  selectedResourceId?: string;
  setSelectedResourceId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  selectedUnit?: string;
  units: string[];
  setSelectedUnit: React.Dispatch<React.SetStateAction<string | undefined>>;
  medicalResults: ResultHistory[] | undefined;
  loading: boolean;
  withExplanation?: boolean;
  withGraph?: boolean;
  /** When set, limits comment to N lines in compact layouts (e.g. lab results row). */
  observationCommentLineClamp?: number;
  /**
   * Inline range view only: hide the observation comment when a primary value is already
   * shown next to the range (`getObservationValueDisplay`, same as ValueBadge), except
   * when the value is FHIR `valueString` — then the comment appears in the same row
   * beside the value (no separate “Comment” block). Modal passes false/undefined.
   */
  observationCommentOnlyIfNoValueDisplay?: boolean;
  /** If set, notifies Bugsnag once when loading finishes and there are no rows to show. */
  noResultsBugsnagContext?: ClinicalImpressionAttachmentNoResultsBugsnagContext;
  /** Enable the personalized/standardized lens toggle when annotation data is present. */
  personalizedResultsEnabled?: boolean;
}

interface RangeViewModalProps {
  dimension: MeasurementCode;
  patient: fhir.Patient;
  isOpen: boolean;
  onClose: () => void;
  initialSelectedResourceId?: string;
  /** When /history is empty, use rows already loaded from Lab Results (v3 + fallback). */
  prefetchedMedicalResults?: ResultHistory[];
  source?: MedicalResultSource;
}

interface InlineRangeViewProps {
  dimension: MeasurementCode;
  patient: fhir.Patient;
  withGraph: boolean;
  showPending?: boolean;
  source?: MedicalResultSource;
}

type MedicalResultTypeValueProps = {
  observation: fhir.Observation;
};

const MedicalResultTextValue = ({
  observation,
}: MedicalResultTypeValueProps) => {
  const classes = useStyles();

  return (
    <div className={classes.valueRoot}>
      <Text size="body">
        {moment(
          observation.effectiveDateTime ||
            observation.effectivePeriod?.end ||
            observation.effectivePeriod?.start,
        ).format('M/D/YYYY')}
      </Text>
      <Text size="body">{getObservationValueDisplay(observation)}</Text>
    </div>
  );
};
const medicalResultsService = new MedicalResultsService();

const useData = ({
  patient,
  code,
  system,
  cohort,
  source,
}: {
  patient: fhir.Patient;
  code: string;
  system: string;
  cohort?: string;
  source?: MedicalResultSource;
}) => {
  const activeProject = useActiveProjectId();
  const [fetchMedicalResultsHistory] = useService(
    medicalResultsService.getHistory,
  );

  return useAsync(async () => {
    const response = await fetchMedicalResultsHistory({
      patientId: patient.id as string,
      project: activeProject,
      code,
      system,
      cohort,
      includeClinicOnly: true,
      source,
    });
    return {
      ...response.data,
      items: formatResultHistoryCodingForDisplay(
        response.data.items,
        source === 'memberUpload',
      ),
    };
  });
};

export const RangeViewContent = ({
  initialSelectedResourceId,
  selectedResourceId,
  setSelectedResourceId,
  selectedUnit,
  setSelectedUnit,
  units,
  loading,
  medicalResults,
  withExplanation = true,
  withGraph = true,
  observationCommentLineClamp,
  observationCommentOnlyIfNoValueDisplay = false,
  noResultsBugsnagContext,
  personalizedResultsEnabled = false,
}: RangeViewContentProps) => {
  const colors = useContext(ColorContext);
  const classes = useStyles();
  const observationCommentTextClasses = useObservationCommentTextStyles({
    lineClamp: observationCommentLineClamp,
  });
  const [selectedTimeScale, setSelectedTimeScale] = useState<TimeScale>('y');
  const [didCalculateDefaultTimeScale, setDidCalculateDefaultTimeScale] =
    useState(false);

  const [resultViewMode, setResultViewMode] =
    useState<ResultViewMode>('standard');

  const showLensToggle = useMemo(
    () =>
      personalizedResultsEnabled &&
      (medicalResults ?? []).some(hasMeaningfulPersonalizedDetail),
    [personalizedResultsEnabled, medicalResults],
  );

  const { formatMessage } = useIntl();
  const reportInfoToBugsnag = useReportInfoToBugsnag();
  const reportedClinicalImpressionNoResultsRef = useRef(false);

  useEffect(() => {
    if (
      loading ||
      (medicalResults && medicalResults.length > 0) ||
      !noResultsBugsnagContext
    ) {
      return;
    }
    if (reportedClinicalImpressionNoResultsRef.current) {
      return;
    }
    reportedClinicalImpressionNoResultsRef.current = true;
    const { code, system, patientId, source, context } =
      noResultsBugsnagContext;
    reportInfoToBugsnag(
      'Clinical impression attachment: medical results showed No results (empty history for searched code)',
      {
        metaData: {
          clinicalImpressionAttachment: {
            code,
            system,
            patientId: patientId ?? '',
            source: source ?? '',
            context,
          },
        },
      },
    );
  }, [loading, medicalResults, noResultsBugsnagContext, reportInfoToBugsnag]);
  const colorsToUse = {
    ...colors,
    ...{
      data: getColorByStatus('default'),
    },
  };

  useEffect(() => {
    if (units.length > 0 && selectedUnit === undefined) {
      setSelectedUnit(units[0]);
    }
  }, [units, selectedUnit, setSelectedUnit]);

  const selectedResult = useMemo(() => {
    return medicalResults?.find(_ => _.resource?.id === selectedResourceId);
  }, [medicalResults, selectedResourceId]);

  const allResultsHaveNumericValues = medicalResults?.every(
    _ => getObservationNumericValue(_.resource) !== null,
  );

  // ensure we always show a default time scale with visible data points
  useEffect(() => {
    if (didCalculateDefaultTimeScale) return;
    if (
      !selectedResult?.resource?.effectiveDateTime &&
      !selectedResult?.resource?.effectivePeriod?.end &&
      !selectedResult?.resource?.effectivePeriod?.start
    )
      return;
    const effectiveDateTime =
      selectedResult.resource.effectiveDateTime ||
      selectedResult.resource.effectivePeriod?.end ||
      selectedResult.resource.effectivePeriod?.start;
    const within1Year = moment(effectiveDateTime).isBetween(
      moment().subtract(1, 'year'),
      moment(),
    );
    if (within1Year) {
      setSelectedTimeScale('y');
    } else {
      // default take the largest time range
      setSelectedTimeScale('5y');
    }
    setDidCalculateDefaultTimeScale(true);
  }, [medicalResults, selectedResult, didCalculateDefaultTimeScale]);

  const handleClick = useCallback(
    (datum: Datum) => {
      setSelectedResourceId(datum.id);
    },
    [setSelectedResourceId],
  );

  const buildExplanation = useCallback(
    (aboutText: string, interpretationText: string = '') => {
      if (!interpretationText) return aboutText;
      return `${interpretationText}${`\n\n${aboutText}`}`;
    },
    [],
  );

  const observationComment = useMemo(() => {
    if (!selectedResult?.resource) {
      return undefined;
    }
    return getObservationComment(selectedResult.resource);
  }, [selectedResult]);

  const showObservationComment =
    Boolean(observationComment && selectedResult?.resource) &&
    (!observationCommentOnlyIfNoValueDisplay ||
      !selectedResult?.result ||
      getObservationValueDisplay(selectedResult.resource).trim() === '' ||
      observationPrimaryValueIsValueString(selectedResult.resource));

  const inlineCommentBesideValue =
    Boolean(observationComment && selectedResult?.resource) &&
    Boolean(observationCommentOnlyIfNoValueDisplay) &&
    selectedResult != null &&
    observationPrimaryValueIsValueString(selectedResult.resource);

  const showObservationCommentBelow =
    showObservationComment && !inlineCommentBesideValue;

  const activeViewMode = showLensToggle ? resultViewMode : 'standard';

  const annotatedDisplay = useMemo(() => {
    if (!selectedResult) return undefined;
    return getAnnotatedHistoryDisplayModel(selectedResult, activeViewMode);
  }, [selectedResult, activeViewMode]);

  const explanation = useMemo(() => {
    if (!selectedResult) return '';
    const interpretationText =
      annotatedDisplay?.interpretationText ??
      (selectedResult.type !== 'pending'
        ? selectedResult.result?.interpretationText
        : undefined);
    const effectiveStatus =
      annotatedDisplay?.effectiveStatus ?? selectedResult.result.status;
    switch (effectiveStatus) {
      case 'attention':
      case 'in-range':
      case 'optimal':
      case 'default':
        return buildExplanation(
          selectedResult.aboutText,
          interpretationText ?? '',
        );

      default:
        return '';
    }
  }, [selectedResult, annotatedDisplay, buildExplanation]);

  useEffect(() => {
    if (!medicalResults || medicalResults.length === 0) {
      return;
    }
    const selectedResult =
      medicalResults.find(_ => _.resource?.id === initialSelectedResourceId) ??
      medicalResults[0];
    if (selectedResult.resource?.id) {
      setSelectedResourceId(selectedResult.resource.id);
    }
  }, [medicalResults, initialSelectedResourceId, setSelectedResourceId]);

  const displayDateTime = useCallback(
    (observation: fhir.Observation, isSelected?: boolean) => (
      <Box
        direction="row"
        align="center"
        gap={2}
        className={isSelected ? classes.selectedResultBox : undefined}
      >
        <Text
          weight="regular"
          size="body"
          className={isSelected ? classes.selectedResultText : undefined}
        >
          {getObservationMMMYYYYDate(observation)?.toUpperCase()}
        </Text>
        <Text
          size="subbody"
          className={isSelected ? classes.selectedResultText : undefined}
        >
          {getObservationHHmmDate(observation)}
        </Text>
      </Box>
    ),
    [classes.selectedResultText, classes.selectedResultBox],
  );

  const multiRange = medicalResults && (
    <div role="presentation" onClick={e => e.stopPropagation()}>
      <Select
        className={classes.resultPicker}
        label=""
        aria-label={formatMessage(messages.selectResult)}
        onChange={resourceId => {
          handleClick({ id: resourceId });
        }}
        value={selectedResult?.resource?.id}
        popoverStyle={{ width: 400 }}
        selectedOptionDisplay={opt => {
          const result = medicalResults.find(
            ({ resource }) => resource?.id === opt.value,
          );
          if (!result) return '';
          return (
            <Box direction="column">
              {displayDateTime(result.resource)}
              <Text size="caption" className={classes.resultPickerLab}>
                {getFormattedLabName(result.resource, formatMessage)}
              </Text>
            </Box>
          );
        }}
      >
        {medicalResults.map(result => {
          const value = getObservationValueDisplay(result.resource);
          const isPending = result.resource?.status === 'preliminary';
          const defaultMessage = isPending ? messages.pending : messages.view;
          const displayValue = value ?? formatMessage(defaultMessage);
          const isSelected = selectedResourceId === result.resource?.id;
          const effectiveStatus = getEffectiveStatusForLens(
            result,
            activeViewMode,
          );
          return (
            <SelectOption key={result.resource.id} value={result.resource.id!}>
              <Box
                direction="row"
                width={800}
                gap={1}
                className={classes.selectionOptionBox}
                align="center"
              >
                {displayDateTime(result.resource, isSelected)}
                <Text
                  size="caption"
                  className={clsx(
                    classes.resultPickerLab,
                    isSelected && classes.selectedResultText,
                  )}
                >
                  {getFormattedLabName(result.resource, formatMessage)}
                </Text>
                <ValueBadge
                  status={effectiveStatus}
                  value={displayValue}
                  colors={colorsToUse}
                />
              </Box>
            </SelectOption>
          );
        })}
      </Select>
    </div>
  );

  return (
    <Box fullWidth direction="column" className={classes.root}>
      {loading ? (
        <Skeleton
          className={classes.skeleton}
          animation="shine"
          height={withGraph ? 574 : 107}
          fullWidth
        />
      ) : medicalResults && medicalResults.length > 0 ? (
        <>
          <Box
            className={classes.topSection}
            direction="column"
            justify="center"
          >
            {withExplanation ? null : (
              <Text weight="bold" className={classes.inlineTitle}>
                {selectedResult?.coding.display}
              </Text>
            )}
            {withGraph ? (
              <>
                {units.length > 1 && (
                  <Box
                    align="center"
                    justify="center"
                    marginTop={2}
                    marginBottom={2}
                  >
                    <UnitPicker
                      selectedUnit={selectedUnit ?? units[0]}
                      units={units}
                      onSelect={setSelectedUnit}
                    />
                  </Box>
                )}
                {allResultsHaveNumericValues &&
                (selectedUnit !== undefined || units.length === 1) ? (
                  <>
                    <Box
                      align="center"
                      justify="center"
                      marginTop={2}
                      marginBottom={2}
                    >
                      <TimeScalePicker
                        selectedScale={selectedTimeScale}
                        onSelect={setSelectedTimeScale}
                      />
                    </Box>

                    <ResultChart
                      key={
                        units.length > 1
                          ? `${selectedUnit ?? units[0]}-${units.join('|')}`
                          : undefined
                      }
                      medicalResults={medicalResults}
                      selectedTimeScale={selectedTimeScale}
                      onResultClick={handleClick}
                      selectedResourceId={selectedResourceId}
                      resultViewMode={activeViewMode}
                    />
                  </>
                ) : (
                  <Box marginLeft={2} direction="column">
                    {medicalResults &&
                      !allResultsHaveNumericValues &&
                      medicalResults.map(result => (
                        <MedicalResultTextValue
                          key={result.resource?.id}
                          observation={result.resource}
                        />
                      ))}
                  </Box>
                )}
              </>
            ) : null}

            {showLensToggle && (
              <Box className={classes.lensToggle}>
                <RadioGroupMinimal
                  title={formatMessage(messages.resultView)}
                  aria-label={formatMessage(messages.resultView)}
                  value={resultViewMode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setResultViewMode(e.target.value as ResultViewMode)
                  }
                  direction="row"
                >
                  <Radio
                    value="standard"
                    label={formatMessage(messages.standard)}
                  />
                  <Radio
                    value="personalized"
                    label={formatMessage(messages.personalized)}
                  />
                </RadioGroupMinimal>
              </Box>
            )}

            <Box
              fullWidth
              gap={2}
              justify="space-between"
              marginBottom={withGraph ? 2 : 0}
              marginTop={withGraph ? 4 : 0}
              paddingLeft={withGraph ? 1.5 : 0}
              paddingRight={withGraph ? 1.5 : 0}
              className={classes.multiRangeContainer}
            >
              {multiRange}
              <Box fullWidth>
                {selectedResult &&
                  (inlineCommentBesideValue ? (
                    <Box className={classes.inlineValueAndCommentRow} fullWidth>
                      <Box>
                        <MultiReferenceRange
                          result={{
                            ...selectedResult,
                            ...selectedResult.result,
                            ranges:
                              annotatedDisplay?.rangeSource.ranges ??
                              ({} as Ranges),
                            status:
                              annotatedDisplay?.effectiveStatus ??
                              selectedResult.result.status,
                            resource: selectedResult.resource,
                          }}
                          value={getObservationValueDisplay(
                            selectedResult.resource,
                          )}
                          variant="inline"
                          colors={{
                            data: getColorByStatus('default'),
                            bad: getColorByStatus('attention'),
                            normal: getColorByStatus('in-range'),
                          }}
                        />
                      </Box>
                      <Box
                        data-testid="range-view-observation-comment"
                        direction="column"
                      >
                        <Text
                          size="body"
                          className={
                            observationCommentTextClasses.observationCommentText
                          }
                        >
                          {observationComment}
                        </Text>
                      </Box>
                    </Box>
                  ) : (
                    <MultiReferenceRange
                      result={{
                        ...selectedResult,
                        ...selectedResult.result,
                        ranges:
                          annotatedDisplay?.rangeSource.ranges ??
                          ({} as Ranges),
                        status:
                          annotatedDisplay?.effectiveStatus ??
                          selectedResult.result.status,
                        resource: selectedResult.resource,
                      }}
                      value={getObservationValueDisplay(
                        selectedResult.resource,
                      )}
                      variant="inline"
                      colors={{
                        data: getColorByStatus('default'),
                        bad: getColorByStatus('attention'),
                        normal: getColorByStatus('in-range'),
                      }}
                    />
                  ))}
              </Box>
            </Box>

            {annotatedDisplay?.providerQuote && (
              <Box
                className={classes.providerQuote}
                marginLeft={withGraph ? 1.5 : 0}
                marginRight={withGraph ? 1.5 : 0}
                fullWidth
              >
                <Text size="body" className={classes.providerQuoteText}>
                  {annotatedDisplay.providerQuote}
                </Text>
              </Box>
            )}
          </Box>

          {showObservationCommentBelow ? (
            <Box
              data-testid="range-view-observation-comment"
              direction="column"
              gap={1}
              marginTop={2}
              paddingLeft={withGraph ? 1.5 : 0}
              paddingRight={withGraph ? 1.5 : 0}
              fullWidth
            >
              <Text weight="bold" size="subbody">
                {formatMessage(messages.comment)}
              </Text>
              <Text
                size="body"
                className={observationCommentTextClasses.observationCommentText}
              >
                {observationComment}
              </Text>
            </Box>
          ) : null}

          {withExplanation ? <MarkdownViewer markdown={explanation} /> : null}
        </>
      ) : (
        <Text className={classes.noResultsText} size="subbody" weight="regular">
          {formatMessage(messages.noResults)}
        </Text>
      )}
    </Box>
  );
};

const RangeViewModalV2: React.FC<RangeViewModalProps> = ({
  isOpen,
  onClose,
  dimension,
  patient,
  source,
  initialSelectedResourceId: initialResourceIdFromProps,
  prefetchedMedicalResults,
}) => {
  const isMobile = useIsMobile();
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const getFeatureFlag = useFeatures();
  const personalizedResultsEnabled = getFeatureFlag(
    'personalizedSubjectResults',
  );

  const cohort = useActiveProjectCohortId();

  const [selectedUnit, setSelectedUnit] = useState<string | undefined>(
    undefined,
  );
  const [selectedResourceId, setSelectedResourceId] = useState<
    string | undefined
  >();

  const { value, loading } = useData({
    patient,
    code: dimension.codes[0].key as string,
    system: dimension.codes[0].system as string,
    cohort,
    source,
  });

  const historyItems = useMemo((): ResultHistory[] => {
    const apiItems = value?.items ?? [];
    if (apiItems.length > 0) {
      return apiItems;
    }
    return prefetchedMedicalResults ?? [];
  }, [value?.items, prefetchedMedicalResults]);

  const showLoading = loading && historyItems.length === 0;

  /**
   * The filter causes a new array to be returned every render. Use useMemo here to
   * avoid re-rendering the chart on every chart interaction (scale change, point selection).
   */
  const [medicalResults, units] = useMemo(() => {
    const results = historyItems.filter(_ => _.resource?.status === 'final');
    const resultsByUnit = groupMedicalResultsByUnit(results ?? []);
    return [
      selectedUnit !== undefined ? resultsByUnit[selectedUnit] ?? [] : results,
      Object.keys(resultsByUnit).sort(),
    ];
  }, [historyItems, selectedUnit]);

  const selectedResult = useMemo(() => {
    return medicalResults?.find(_ => _.resource?.id === selectedResourceId);
  }, [medicalResults, selectedResourceId]);

  const hasMultipleResults = medicalResults ? medicalResults.length > 1 : false;
  const selectedResourceIndex = medicalResults?.findIndex(
    _ => _.resource?.id === selectedResourceId,
  );
  // The results are sorted from most recent to least recent
  const hasNextResult = hasMultipleResults && selectedResourceIndex !== 0;
  const hasPreviousResult =
    hasMultipleResults && selectedResourceIndex !== medicalResults!.length - 1;
  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onClose}
      size={1}
      isFullScreen={isMobile}
      contentClassName={!isMobile ? classes.modalContent : undefined}
      data-testid="range-view-modal"
      title={selectedResult?.coding.display}
      fullWidth
      actions={
        hasMultipleResults && (
          <Box justify="space-between" fullWidth>
            <IconButton
              aria-label={formatMessage(messages.previous)}
              icon={ArrowLeft}
              onClick={() => {
                if (selectedResourceIndex !== undefined) {
                  setSelectedResourceId(
                    medicalResults![selectedResourceIndex + 1].resource.id!,
                  );
                }
              }}
              disabled={!hasPreviousResult}
            />
            <IconButton
              aria-label={formatMessage(messages.next)}
              icon={ArrowRight}
              onClick={() => {
                if (selectedResourceIndex !== undefined) {
                  setSelectedResourceId(
                    medicalResults![selectedResourceIndex - 1].resource.id!,
                  );
                }
              }}
              disabled={!hasNextResult}
            />
          </Box>
        )
      }
    >
      <RangeViewContent
        medicalResults={medicalResults}
        loading={showLoading}
        initialSelectedResourceId={initialResourceIdFromProps}
        selectedResourceId={selectedResourceId}
        setSelectedResourceId={setSelectedResourceId}
        selectedUnit={selectedUnit}
        setSelectedUnit={setSelectedUnit}
        units={units}
        personalizedResultsEnabled={personalizedResultsEnabled}
      />
    </Modal>
  );
};

export function InlineRangeView({
  dimension,
  patient,
  withGraph = true,
  showPending = false,
  source,
}: InlineRangeViewProps) {
  const cohort = useActiveProjectCohortId();
  const classes = useStyles();

  const [selectedUnit, setSelectedUnit] = useState<string | undefined>(
    undefined,
  );
  const [selectedResourceId, setSelectedResourceId] = useState<
    string | undefined
  >();

  const { value, loading } = useData({
    patient,
    code: dimension.codes[0].key as string,
    system: dimension.codes[0].system as string,
    cohort,
    source,
  });

  /**
   * The filter causes a new array to be returned every render. Use useMemo here to
   * avoid re-rendering the chart on every chart interaction (scale change, point selection).
   */
  const [medicalResults, units] = useMemo(() => {
    const results = value?.items.filter(
      _ => _.resource?.status === 'final' || showPending,
    );
    const resultsByUnit = groupMedicalResultsByUnit(results ?? []);
    return [
      selectedUnit !== undefined ? resultsByUnit[selectedUnit] ?? [] : results,
      Object.keys(resultsByUnit).sort(),
    ];
  }, [value?.items, selectedUnit, showPending]);

  return (
    <Box className={classes.inlineRangeView}>
      <RangeViewContent
        medicalResults={medicalResults}
        loading={loading}
        selectedResourceId={selectedResourceId}
        setSelectedResourceId={setSelectedResourceId}
        withExplanation={false}
        withGraph={withGraph}
        observationCommentLineClamp={withGraph ? undefined : 4}
        observationCommentOnlyIfNoValueDisplay
        selectedUnit={selectedUnit}
        setSelectedUnit={setSelectedUnit}
        units={units}
      />
    </Box>
  );
}

function InlineRangeViewWithPrefetchedData({
  medicalResults,
  withGraph,
  showPending,
  selectedUnit: selectedUnitProp,
  onUnitChange,
  noResultsBugsnagContext,
}: {
  medicalResults: ResultHistory[];
  withGraph?: boolean;
  showPending?: boolean;
  /** When set with `onUnitChange`, unit selection is controlled (e.g. shared with an outer unit picker). */
  selectedUnit?: string;
  onUnitChange?: (unit: string) => void;
  noResultsBugsnagContext?: ClinicalImpressionAttachmentNoResultsBugsnagContext;
}) {
  const classes = useStyles();

  const isUnitControlled = onUnitChange !== undefined;

  const [selectedUnitInternal, setSelectedUnitInternal] = useState<
    string | undefined
  >(undefined);

  const selectedUnit = isUnitControlled
    ? selectedUnitProp!
    : selectedUnitInternal;

  const setSelectedUnitForContent = useCallback(
    (action: React.SetStateAction<string | undefined>) => {
      if (onUnitChange !== undefined) {
        const next =
          typeof action === 'function' ? action(selectedUnitProp) : action;
        if (next !== undefined) {
          onUnitChange(next);
        }
      } else {
        setSelectedUnitInternal(action);
      }
    },
    [onUnitChange, selectedUnitProp],
  );

  const [selectedResourceId, setSelectedResourceId] = useState<
    string | undefined
  >();

  const [filteredMedicalResults, units] = useMemo(() => {
    const results = medicalResults.filter(
      _ => _.resource?.status === 'final' || showPending,
    );
    const resultsByUnit = groupMedicalResultsByUnit(results ?? []);
    return [
      selectedUnit !== undefined ? resultsByUnit[selectedUnit] ?? [] : results,
      Object.keys(resultsByUnit).sort(),
    ];
  }, [medicalResults, selectedUnit, showPending]);

  return (
    <Box className={classes.inlineRangeView}>
      <RangeViewContent
        medicalResults={filteredMedicalResults}
        loading={false}
        selectedResourceId={selectedResourceId}
        setSelectedResourceId={setSelectedResourceId}
        withExplanation={false}
        withGraph={withGraph}
        observationCommentLineClamp={withGraph ? undefined : 4}
        observationCommentOnlyIfNoValueDisplay
        selectedUnit={selectedUnit}
        setSelectedUnit={setSelectedUnitForContent}
        units={units}
        noResultsBugsnagContext={noResultsBugsnagContext}
      />
    </Box>
  );
}

InlineRangeView.WithPrefetchedData = InlineRangeViewWithPrefetchedData;

export default React.memo(RangeViewModalV2);
